import type {
  InfiniteData,
  Query,
  QueryClient,
  QueryKey,
} from "@tanstack/react-query";
import {
  CONFIGURE_DEBOUNCE_MS,
  CONFIGURE_READY_TIMEOUT_MS,
  CONTENT_HUB_ADAPTER_URL,
  CONTENT_HUB_EVENTS_KEY,
  CONTENT_HUB_SEARCH_KEY,
  MAX_FIRST_PAGE,
  contentHubUrl,
} from "./config";
import {
  ContentHubConfigureError,
  ContentHubConnectionGoneError,
  configureContentHubStream,
  type StreamFilterConfig,
} from "./api";
import { fetchStreamPassUrl } from "./client";
import type { FilterTreeNode } from "./filter";
import { mapStreamFrame } from "./mappers";
import type {
  ContentHubConnectedFrame,
  ContentHubConnectionMetrics,
  ContentHubItem,
  ContentHubMatchFrame,
  ContentHubPage,
  ContentHubStreamFrame,
  ContentHubStreamStatus,
} from "./types";

type EventsInfiniteData = InfiniteData<ContentHubPage>;

/** Settlement status of a live filter, used to gate its search query so the
 *  view fetches page one only after `configure` (or a fallback). */
export type FilterStatus = "pending" | "ready" | "errored" | "unknown";

interface DesiredFilter {
  tree: FilterTreeNode;
  queryKey: QueryKey;
  refCount: number;
}

/**
 * Prepend/upsert a live item into an infinite-query cache: replace in place on
 * any page if already present (an update/correction), else prepend to the first
 * (newest) page, capped so a live burst can't grow it without bound. Shared by
 * unfiltered `all_events` patching and filtered `match` patching.
 */
function patchInfinitePages(
  old: EventsInfiniteData | undefined,
  item: ContentHubItem
): EventsInfiniteData | undefined {
  if (!old || old.pages.length === 0) return old;

  for (let p = 0; p < old.pages.length; p++) {
    const i = old.pages[p].items.findIndex(
      (e) => e.publishId === item.publishId
    );
    if (i >= 0) {
      const pages = [...old.pages];
      const items = [...pages[p].items];
      items[i] = { ...item, wasUpdated: true };
      pages[p] = { ...pages[p], items };
      return { ...old, pages };
    }
  }

  const first = old.pages[0];
  const pages = [
    { ...first, items: [item, ...first.items].slice(0, MAX_FIRST_PAGE) },
    ...old.pages.slice(1),
  ];
  return { ...old, pages };
}

/** Match any base-feed events query whose (sorted) source list contains the
 *  given backend_source, so one frame patches every view that includes it
 *  (single-source widgets and the combined stocks feed alike). */
const eventsQueryMatches =
  (backendSource: string) =>
  (query: Query): boolean => {
    const key = query.queryKey;
    return (
      Array.isArray(key) &&
      key[0] === CONTENT_HUB_EVENTS_KEY[0] &&
      key[1] === CONTENT_HUB_EVENTS_KEY[1] &&
      Array.isArray(key[2]) &&
      (key[2] as string[]).includes(backendSource)
    );
  };

/** localStorage key for the user's sticky "stay on polling" preference. When
 *  set, the stream never opens SSE and pins to the polling fallback. */
const PREFER_POLLING_KEY = "contenthub:prefer-polling";

const INITIAL_RECONNECT_DELAY_MS = 1_000;
const MAX_RECONNECT_DELAY_MS = 30_000;
const CONFIGURE_RETRY_DELAY_MS = 2_000;
/** How long after the socket opens to wait for a `connected` data frame before
 *  concluding the adapter doesn't support live filtering (older deployments
 *  send only an SSE `: connected` comment). Determined once at startup, so
 *  later filter activations gate with no artificial delay. */
const HANDSHAKE_TIMEOUT_MS = 1_000;
/** Lifetime of a one-time stream pass. If the socket hasn't gone live within
 *  this window of receiving a pass, the pass is dead — discard the attempt and
 *  restart the two-step exchange with a fresh pass. Matches the adapter's TTL. */
const STREAM_PASS_TTL_MS = 30_000;
/** Grace period before closing the connection once the last subscriber
 *  unmounts — absorbs React effect unmount/remount cycles (StrictMode,
 *  route transitions) without churning the SSE connection. */
const SHUTDOWN_DELAY_MS = 3_000;

/**
 * Master switch for the no-activity watchdog. The adapter sends a keepalive
 * ping as a real data frame every ~20 seconds, so even a quiet feed produces a
 * steady pulse that resets the activity clock and the watchdog only fires on a
 * genuine stall. Kept as a kill switch: turning it off disables both the stall
 * watchdog and the visibility-return liveness probe.
 *
 * The connection-level failure signals (failed connect, early drop, pass expiry)
 * are independent of this flag and always active.
 */
const ACTIVITY_WATCHDOG_ENABLED = true;
/** No-activity watchdog window: if no frame arrives on an open stream for this
 *  long, treat it as a stall, count a failure, and reconnect. Any message frame
 *  resets it — a headline, a match, the connected handshake, or a keepalive
 *  ping. Sized to a few missed pings so a brief gap doesn't trip it. */
const ACTIVITY_TIMEOUT_MS = 75_000;
/** After the tab returns to the foreground, how long to wait for a keepalive
 *  ping (or any frame) before deciding the socket died silently while hidden and
 *  reconnecting. A couple of ping intervals, so a healthy connection proves
 *  itself quickly — this replaces the old blunt "hidden too long" reconnect. */
const VISIBILITY_GRACE_MS = 40_000;
/** A connection must stay live this long after its `connected` frame before its
 *  failure streak is forgiven. A socket that drops (or stalls) within this
 *  window still counts toward the fallback threshold — so "connected, then
 *  dropped almost immediately" is correctly treated as a failure, not success. */
const STABILITY_WINDOW_MS = 30_000;
/** Consecutive failures — failed connects, early drops, pass expiries, or
 *  activity stalls — before the feed falls back to polling. */
const FALLBACK_THRESHOLD = 3;
/** Poll cadence while in fallback mode: refetch the active content-hub queries
 *  (page one of /v1/search, newest-first) so new events keep surfacing while
 *  the live stream is down. */
const POLL_INTERVAL_MS = 30_000;

/**
 * Owns the single shared SSE connection to GET /v1/stream?feed=all.
 *
 * Unfiltered feeds: `all_events` frames are routed by backend_source into every
 * matching base-feed query. Live filtered feeds: active filters are registered
 * server-side via POST /v1/stream/configure (replace semantics, keyed by the
 * client filter id) and their matches arrive as `match` frames on the same
 * socket, patched into the filter's search cache. The connection is
 * ref-counted; reconnects use capped exponential backoff and, on a new
 * connection_id, re-configure the desired filter set before recovering.
 */
class ContentHubStreamManager {
  private eventSource: EventSource | null = null;
  /** Guards against overlapping connects while the pass exchange is in flight
   *  (connect() is async), so a re-entrant subscribe/reconnect can't open a
   *  second socket. */
  private connecting = false;
  /** Watchdog for the one-time pass's TTL: fires if the socket doesn't go live
   *  before the pass expires, triggering a fresh two-step reconnect. */
  private passTimer: ReturnType<typeof setTimeout> | null = null;
  private queryClient: QueryClient | null = null;
  private subscriberCount = 0;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private shutdownTimer: ReturnType<typeof setTimeout> | null = null;
  private hasConnectedOnce = false;
  private status: ContentHubStreamStatus = "idle";
  private statusListeners = new Set<() => void>();

  // ── Metrics for the global status panel ────────────────────────────────────
  /** When the current live connection was established; null while not connected. */
  private connectedSince: number | null = null;
  /** Last frame of any kind (proof of life). */
  private lastFrameAt: number | null = null;
  /** Last actual event delivered to a feed (excludes handshake/keepalive). */
  private lastEventAt: number | null = null;
  /** When the next reconnect attempt will fire; null when not waiting to retry. */
  private nextRetryAt: number | null = null;

  // ── Live-filter state ──────────────────────────────────────────────────
  private connectionId: string | null = null;
  /** null = unknown, true = adapter sent a `connected` frame, false = it didn't
   *  (older deployment) so live filtering is unavailable and gating is skipped. */
  private liveSupported: boolean | null = null;
  private handshakeTimer: ReturnType<typeof setTimeout> | null = null;
  private desired = new Map<string, DesiredFilter>();
  /** Ids the adapter rejected (422); excluded from configure until re-registered. */
  private erroredIds = new Set<string>();
  private filterStatus = new Map<string, FilterStatus>();
  private filterStatusListeners = new Set<() => void>();
  private readyTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private configureTimer: ReturnType<typeof setTimeout> | null = null;
  private configureRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private configureInFlight = false;
  private configureDirty = false;
  /** Id-set last successfully sent, to skip no-op configure calls. */
  private lastConfiguredKey = "";

  // ── Activity watchdog + polling fallback ───────────────────────────────────
  /** Timestamp of the last frame received (any type) — the watchdog's clock. */
  private lastActivityAt = 0;
  private watchdogTimer: ReturnType<typeof setInterval> | null = null;
  /** Consecutive failures since the last stable connection; drives fallback. */
  private failureCount = 0;
  private stabilityTimer: ReturnType<typeof setTimeout> | null = null;
  private pollingMode = false;
  private pollingTimer: ReturnType<typeof setInterval> | null = null;
  private pollingModeListeners = new Set<() => void>();
  /** When the last polling refetch completed; null before the first one. */
  private lastPolledAt: number | null = null;
  /** When the next polling refetch is due, while in polling mode. */
  private nextPollAt: number | null = null;
  /** User preference to stay on polling and never open SSE (persisted). */
  private preferPolling = false;
  private preferPollingListeners = new Set<() => void>();

  // ── Visibility-based recovery ──────────────────────────────────────────────
  /** True while the tab is hidden — timers freeze and delivery throttles, so the
   *  watchdog must stand down and let a visibility-return probe drive recovery. */
  private isHidden = false;
  /** One-shot probe armed on tab-return: reconnects only if no frame arrives
   *  within the grace window, meaning the socket dropped silently while hidden. */
  private visibilityGraceTimer: ReturnType<typeof setTimeout> | null = null;
  private visibilityBound: (() => void) | null = null;

  constructor() {
    this.preferPolling = this.readPreferPolling();
    // Registered once for the manager's lifetime (module singleton — lives until
    // page unload), so it is intentionally never removed.
    this.startVisibilityTracking();
  }

  /** Register a subscriber. Starts the connection if it's the first one.
   *  Returns an unsubscribe function. */
  subscribe(queryClient: QueryClient): () => void {
    this.queryClient = queryClient;
    this.subscriberCount += 1;

    if (this.shutdownTimer) {
      clearTimeout(this.shutdownTimer);
      this.shutdownTimer = null;
    }
    if (this.preferPolling) {
      // Sticky preference: skip SSE entirely and pin to polling.
      this.enterPollingMode();
    } else if (!this.eventSource && !this.reconnectTimer && !this.connecting) {
      void this.connect();
    }

    let unsubscribed = false;
    return () => {
      if (unsubscribed) return;
      unsubscribed = true;
      this.subscriberCount -= 1;
      if (this.subscriberCount <= 0) {
        this.scheduleShutdown();
      }
    };
  }

  /**
   * Register a live filter (ref-counted). The caller must also `subscribe` to
   * keep the socket alive. Returns an unregister that drops the filter and
   * reconfigures the desired set.
   */
  registerFilter(id: string, tree: FilterTreeNode, queryKey: QueryKey): () => void {
    const existing = this.desired.get(id);
    if (existing) {
      existing.refCount += 1;
      existing.tree = tree;
      existing.queryKey = queryKey;
    } else {
      this.desired.set(id, { tree, queryKey, refCount: 1 });
      // Gate the query until configure resolves — unless the adapter isn't
      // configured, or we've already determined it doesn't support live
      // filtering, in which case fetch immediately (non-live search).
      const gate = CONTENT_HUB_ADAPTER_URL && this.liveSupported !== false;
      this.setFilterStatus(id, gate ? "pending" : "ready");
      if (gate) this.armReadyTimeout(id);
    }
    this.scheduleConfigure();

    let unregistered = false;
    return () => {
      if (unregistered) return;
      unregistered = true;
      const entry = this.desired.get(id);
      if (!entry) return;
      entry.refCount -= 1;
      if (entry.refCount <= 0) {
        this.desired.delete(id);
        this.erroredIds.delete(id);
        this.clearReadyTimeout(id);
        this.filterStatus.delete(id);
        this.scheduleConfigure();
      }
    };
  }

  /** Current settlement status of a filter (for the query gate). */
  getFilterStatus = (id: string): FilterStatus =>
    this.filterStatus.get(id) ?? "unknown";

  /** Subscribe to filter-status changes (useSyncExternalStore-compatible). */
  onFilterStatusChange = (listener: () => void): (() => void) => {
    this.filterStatusListeners.add(listener);
    return () => this.filterStatusListeners.delete(listener);
  };

  getStatus = (): ContentHubStreamStatus => this.status;

  /** Subscribe to status changes (useSyncExternalStore-compatible). */
  onStatusChange = (listener: () => void): (() => void) => {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  };

  private setStatus(status: ContentHubStreamStatus) {
    if (this.status === status) return;
    this.status = status;
    // Anchor the panel's "connected since" to when the live status began, and
    // clear the retry countdown once we're no longer waiting to reconnect.
    if (status === "connected") {
      this.connectedSince = Date.now();
      this.nextRetryAt = null;
    } else {
      this.connectedSince = null;
      if (status === "idle") this.nextRetryAt = null;
    }
    this.statusListeners.forEach((listener) => listener());
  }

  private setFilterStatus(id: string, status: FilterStatus) {
    if (this.filterStatus.get(id) === status) return;
    this.filterStatus.set(id, status);
    this.filterStatusListeners.forEach((listener) => listener());
  }

  /** After the fallback timeout, let a still-pending filter fetch anyway so a
   *  slow/configure-less adapter degrades to non-live search rather than hang. */
  private armReadyTimeout(id: string) {
    this.clearReadyTimeout(id);
    this.readyTimers.set(
      id,
      setTimeout(() => {
        this.readyTimers.delete(id);
        if (this.filterStatus.get(id) === "pending") {
          this.setFilterStatus(id, "ready");
        }
      }, CONFIGURE_READY_TIMEOUT_MS)
    );
  }

  private clearReadyTimeout(id: string) {
    const t = this.readyTimers.get(id);
    if (t) {
      clearTimeout(t);
      this.readyTimers.delete(id);
    }
  }

  /** Release every still-pending gate — used once we conclude the adapter has
   *  no live-filtering support, so filtered views fall back to plain search. */
  private releasePendingGates() {
    for (const [id, status] of this.filterStatus) {
      if (status === "pending") {
        this.clearReadyTimeout(id);
        this.setFilterStatus(id, "ready");
      }
    }
  }

  private async connect() {
    if (this.preferPolling) return; // user pinned to polling — never open SSE
    if (!CONTENT_HUB_ADAPTER_URL) return; // adapter not configured
    if (this.connecting || this.eventSource) return; // already (re)connecting
    this.connecting = true;
    this.clearReconnectTimer();
    this.nextRetryAt = null; // an attempt is starting now — no pending wait
    this.setStatus(this.hasConnectedOnce ? "reconnecting" : "connecting");

    let eventSource: EventSource;
    try {
      // Step 1: exchange the user's JWT for a short-lived, single-use stream
      // pass. Runs on every (re)connect because the pass is one-time-use.
      // feed=all keeps the firehose all_events frames flowing alongside matches.
      const url = await fetchStreamPassUrl(
        contentHubUrl("v1/stream") + "?feed=all"
      );
      // A shutdown (last subscriber left) may have raced the pass exchange —
      // don't open a stray socket.
      if (this.subscriberCount <= 0 || this.eventSource) return;

      // Step 2: open the SSE stream with the pass.
      eventSource = new EventSource(url);
      this.eventSource = eventSource;
    } catch {
      // The pass exchange failed (network / auth / no pass). Count it as a
      // connect failure, back off, and retry the whole two-step flow.
      this.eventSource = null;
      this.handleFailure();
      this.setStatus("reconnecting");
      this.scheduleReconnect();
      return;
    } finally {
      this.connecting = false;
    }

    // Arm the pass-TTL watchdog: the pass is consumed when the connection goes
    // live, so if the socket doesn't open before it expires, restart with a
    // fresh pass instead of waiting on a connection that can't authenticate.
    this.armPassTimeout(eventSource);

    eventSource.onopen = () => {
      this.clearPassTimeout(); // socket live — the pass was consumed successfully
      this.reconnectAttempt = 0;
      this.setStatus("connected");
      // Socket is open — arm the activity watchdog. Stability/failure-forgiveness
      // is anchored on the `connected` frame (onConnected), the real "live" mark.
      this.recordActivity();
      this.startWatchdog();
      // The connection_id + reconnect recovery are driven by the `connected`
      // frame (below), not onopen, so configure precedes the recovery refetch.
      // If no `connected` frame arrives shortly, the adapter is an older build
      // without live filtering — stop gating so filtered views fetch promptly.
      if (this.liveSupported === null && !this.handshakeTimer) {
        this.handshakeTimer = setTimeout(() => {
          this.handshakeTimer = null;
          if (this.liveSupported === null) {
            this.liveSupported = false;
            this.releasePendingGates();
          }
        }, HANDSHAKE_TIMEOUT_MS);
      }
    };

    eventSource.onmessage = (event: MessageEvent<string>) => {
      this.handleFrame(event.data);
    };

    eventSource.onerror = () => {
      // Manage reconnection ourselves (capped exponential backoff) instead
      // of relying on EventSource's fixed-interval auto-retry.
      this.clearPassTimeout();
      this.stopWatchdog();
      this.clearStabilityReset();
      this.clearVisibilityGrace();
      eventSource.close();
      if (this.eventSource !== eventSource) return;
      this.eventSource = null;
      this.connectionId = null; // old connection's server-side filters are gone
      // A dropped connection is a failure — one that drops before it stabilized
      // (see scheduleStabilityReset) pushes the streak toward polling fallback.
      this.handleFailure();
      this.setStatus("reconnecting");
      this.scheduleReconnect();
    };
  }

  /** Start the pass-TTL watchdog for a just-opened socket. If it fires before
   *  the socket goes live (onopen), the one-time pass has expired unused —
   *  discard this attempt and restart the two-step exchange. */
  private armPassTimeout(eventSource: EventSource) {
    this.clearPassTimeout();
    this.passTimer = setTimeout(() => {
      this.passTimer = null;
      if (this.eventSource !== eventSource) return; // already superseded/closed
      eventSource.close();
      this.stopWatchdog();
      this.clearStabilityReset();
      this.eventSource = null;
      this.connectionId = null;
      // The socket never went live before the one-time pass expired — a failed
      // connect attempt.
      this.handleFailure();
      this.setStatus("reconnecting");
      this.scheduleReconnect();
    }, STREAM_PASS_TTL_MS);
  }

  private clearPassTimeout() {
    if (this.passTimer) {
      clearTimeout(this.passTimer);
      this.passTimer = null;
    }
  }

  private handleFrame(data: string) {
    // Any frame (connected / match / all_events / a future message-ping) is
    // proof of life — reset the activity watchdog before doing anything else.
    this.recordActivity();
    this.lastFrameAt = Date.now();
    let frame: { type?: string };
    try {
      frame = JSON.parse(data);
    } catch {
      return; // malformed frame — ignore
    }
    if (!frame || typeof frame.type !== "string") return;

    // The top-level frame and the `all_events` envelope share a `type: string`
    // field, so narrow by explicit cast per branch rather than union inference.
    switch (frame.type) {
      case "connected":
        this.onConnected((frame as ContentHubConnectedFrame).connection_id);
        return;
      case "match": {
        const m = frame as ContentHubMatchFrame;
        this.onMatch(m.filter_ids, m.event);
        return;
      }
      case "all_events":
        this.onAllEvents(frame as ContentHubStreamFrame);
        return;
      default:
        return;
    }
  }

  /** Handshake / reconnect: capture the connection id, (re)configure the
   *  desired set, then run reconnect recovery once configure settles. */
  private onConnected(connectionId: string) {
    this.connectionId = connectionId;
    this.liveSupported = true;
    this.recordActivity();
    // The connection is confirmed live: forgive the failure streak once it has
    // held for STABILITY_WINDOW_MS, and drop out of polling now that the live
    // stream is flowing again. (A drop before that window keeps the streak.)
    this.scheduleStabilityReset();
    this.exitPollingMode();
    if (this.handshakeTimer) {
      clearTimeout(this.handshakeTimer);
      this.handshakeTimer = null;
    }
    const isReconnect = this.hasConnectedOnce;
    this.hasConnectedOnce = true;
    // A fresh connection has no server-side filters; force a resend.
    this.lastConfiguredKey = "";

    const recover = () => {
      if (!isReconnect) return;
      // Reset both unfiltered and filtered queries to a fresh page one; the
      // reconnect gap is at the head of both feeds.
      this.queryClient?.resetQueries({ queryKey: CONTENT_HUB_EVENTS_KEY });
      this.queryClient?.resetQueries({ queryKey: CONTENT_HUB_SEARCH_KEY });
    };

    if (this.desired.size > 0) {
      this.flushConfigure().finally(recover);
    } else {
      recover();
    }
  }

  private onAllEvents(frame: ContentHubStreamFrame) {
    if (!frame.backend_source) return;
    const item = mapStreamFrame(frame);
    item.receivedAt = Date.now();
    this.lastEventAt = item.receivedAt;

    this.queryClient?.setQueriesData<EventsInfiniteData>(
      { predicate: eventsQueryMatches(frame.backend_source) },
      (old) => patchInfinitePages(old, item)
    );
  }

  private onMatch(filterIds: string[], event: ContentHubStreamFrame) {
    if (!Array.isArray(filterIds) || filterIds.length === 0 || !event) return;
    const item = mapStreamFrame(event);
    item.receivedAt = Date.now();
    this.lastEventAt = item.receivedAt;

    for (const id of filterIds) {
      const entry = this.desired.get(id);
      if (!entry) continue; // raced a reconfigure — drop silently
      this.queryClient?.setQueryData<EventsInfiniteData>(entry.queryKey, (old) =>
        patchInfinitePages(old, item)
      );
    }
  }

  // ── configure lifecycle ────────────────────────────────────────────────

  private scheduleConfigure() {
    if (this.configureTimer) clearTimeout(this.configureTimer);
    this.configureTimer = setTimeout(() => {
      this.configureTimer = null;
      void this.flushConfigure();
    }, CONFIGURE_DEBOUNCE_MS);
  }

  private currentFilterConfigs(): StreamFilterConfig[] {
    const configs: StreamFilterConfig[] = [];
    for (const [id, entry] of this.desired) {
      if (this.erroredIds.has(id)) continue; // exclude rejected trees
      configs.push({ id, tree: entry.tree });
    }
    return configs;
  }

  private async flushConfigure(): Promise<void> {
    if (!this.connectionId) return; // no socket yet — the connected frame flushes
    if (this.configureInFlight) {
      this.configureDirty = true;
      return;
    }

    const filters = this.currentFilterConfigs();
    const key = filters
      .map((f) => f.id)
      .sort()
      .join(",");
    if (key === this.lastConfiguredKey) return; // no change since last ack

    this.configureInFlight = true;
    const connectionId = this.connectionId;
    try {
      await configureContentHubStream(connectionId, filters);
      this.lastConfiguredKey = key;
      // Everything we sent is now active; anything not sent (errored) stays so.
      for (const f of filters) this.setFilterStatus(f.id, "ready");
      this.resetConfigureRetry();
    } catch (err) {
      if (err instanceof ContentHubConfigureError) {
        const badId = err.filterId;
        if (badId && this.desired.has(badId)) {
          // One named bad tree: drop it and resend the rest without it.
          this.erroredIds.add(badId);
          this.setFilterStatus(badId, "errored");
          this.lastConfiguredKey = ""; // force the corrected resend
          this.configureDirty = true;
        } else {
          // The adapter rejected the set but didn't name a filter. Resending the
          // same set would just fail again, so treat it as settled: ungate the
          // queries (they fall back to non-live /v1/search) and don't resend it.
          for (const f of filters) this.setFilterStatus(f.id, "ready");
          this.lastConfiguredKey = key;
        }
      } else if (err instanceof ContentHubConnectionGoneError) {
        // Connection died before we noticed; the reconnect path reconfigures.
        this.connectionId = null;
        this.lastConfiguredKey = "";
      } else {
        // Network/5xx — retry while the socket is alive (idempotent resend).
        this.scheduleConfigureRetry();
      }
    } finally {
      this.configureInFlight = false;
      if (this.configureDirty) {
        this.configureDirty = false;
        void this.flushConfigure();
      }
    }
  }

  private scheduleConfigureRetry() {
    if (this.configureRetryTimer) return;
    this.configureRetryTimer = setTimeout(() => {
      this.configureRetryTimer = null;
      if (this.connectionId && this.subscriberCount > 0) {
        void this.flushConfigure();
      }
    }, CONFIGURE_RETRY_DELAY_MS);
  }

  private resetConfigureRetry() {
    if (this.configureRetryTimer) {
      clearTimeout(this.configureRetryTimer);
      this.configureRetryTimer = null;
    }
  }

  private scheduleReconnect() {
    this.clearReconnectTimer();
    const delay = Math.min(
      INITIAL_RECONNECT_DELAY_MS * 2 ** this.reconnectAttempt,
      MAX_RECONNECT_DELAY_MS
    );
    this.reconnectAttempt += 1;
    this.nextRetryAt = Date.now() + delay;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.subscriberCount > 0) void this.connect();
    }, delay);
  }

  private scheduleShutdown() {
    if (this.shutdownTimer) return;
    this.shutdownTimer = setTimeout(() => {
      this.shutdownTimer = null;
      if (this.subscriberCount > 0) return;
      this.clearReconnectTimer();
      this.resetConfigureRetry();
      if (this.configureTimer) {
        clearTimeout(this.configureTimer);
        this.configureTimer = null;
      }
      if (this.handshakeTimer) {
        clearTimeout(this.handshakeTimer);
        this.handshakeTimer = null;
      }
      this.clearPassTimeout();
      this.stopWatchdog();
      this.clearStabilityReset();
      this.clearVisibilityGrace();
      this.exitPollingMode();
      this.failureCount = 0;
      this.eventSource?.close();
      this.eventSource = null;
      this.connectionId = null;
      this.liveSupported = null;
      this.lastConfiguredKey = "";
      this.reconnectAttempt = 0;
      this.hasConnectedOnce = false;
      this.setStatus("idle");
    }, SHUTDOWN_DELAY_MS);
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  // ── Activity watchdog + polling fallback ───────────────────────────────────

  private recordActivity() {
    this.lastActivityAt = Date.now();
  }

  /** Arm the no-activity watchdog for the current open socket. Any frame resets
   *  the clock (recordActivity); a gap longer than ACTIVITY_TIMEOUT_MS is a
   *  stall — counted as a failure and forced to reconnect. */
  private startWatchdog() {
    this.stopWatchdog();
    // Off until the adapter sends message-type pings (see ACTIVITY_WATCHDOG_ENABLED).
    if (!ACTIVITY_WATCHDOG_ENABLED) return;
    this.lastActivityAt = Date.now();
    this.watchdogTimer = setInterval(() => {
      if (this.status !== "connected") return;
      // Browsers freeze timers + throttle delivery while hidden, so a post-hide
      // tick would read a huge stale gap and fake a stall. Visibility-return
      // handles recovery instead (see handleVisibilityChange).
      if (this.isHidden) return;
      if (Date.now() - this.lastActivityAt > ACTIVITY_TIMEOUT_MS) {
        this.handleFailure();
        this.teardownForReconnect();
      }
    }, Math.max(ACTIVITY_TIMEOUT_MS / 2, 5_000));
  }

  private stopWatchdog() {
    if (this.watchdogTimer) {
      clearInterval(this.watchdogTimer);
      this.watchdogTimer = null;
    }
  }

  /** Close the current socket and schedule a fresh two-step reconnect. Used by
   *  the activity-stall and visibility-return probe paths; onerror inlines the
   *  same steps because it also carries a superseded-socket guard. */
  private teardownForReconnect() {
    this.clearPassTimeout();
    this.stopWatchdog();
    this.clearStabilityReset();
    this.clearVisibilityGrace();
    this.eventSource?.close();
    this.eventSource = null;
    this.connectionId = null;
    this.setStatus("reconnecting");
    this.scheduleReconnect();
  }

  /** Count a failure (failed connect, early drop, pass expiry, or stall). Once
   *  FALLBACK_THRESHOLD consecutive failures accrue, enter polling fallback. The
   *  reconnect loop keeps running underneath — a confirmed `connected` frame
   *  resets the streak and exits polling. */
  private handleFailure() {
    this.failureCount += 1;
    if (!this.pollingMode && this.failureCount >= FALLBACK_THRESHOLD) {
      this.enterPollingMode();
    }
  }

  /** After a connection holds for STABILITY_WINDOW_MS, forgive the failure
   *  streak. A drop/stall before the timer fires leaves the streak intact, so
   *  flapping connections still reach the polling threshold. */
  private scheduleStabilityReset() {
    this.clearStabilityReset();
    this.stabilityTimer = setTimeout(() => {
      this.stabilityTimer = null;
      if (this.status === "connected") this.failureCount = 0;
    }, STABILITY_WINDOW_MS);
  }

  private clearStabilityReset() {
    if (this.stabilityTimer) {
      clearTimeout(this.stabilityTimer);
      this.stabilityTimer = null;
    }
  }

  /** Enter polling fallback: periodically refetch every mounted content-hub
   *  feed/search (page one of /v1/search, newest-first) so new events keep
   *  surfacing while the live stream is down. Only queries with active observers
   *  are refetched, so unmounted widgets cost nothing. */
  private enterPollingMode() {
    if (this.pollingMode) return;
    this.pollingMode = true;
    this.notifyPollingModeListeners();

    const tick = async () => {
      const qc = this.queryClient;
      if (!qc) return;
      // The interval fires on a fixed cadence, so the next poll is one interval
      // out from when this one started, regardless of how long the fetch takes.
      this.nextPollAt = Date.now() + POLL_INTERVAL_MS;
      await Promise.allSettled([
        qc.refetchQueries({ queryKey: CONTENT_HUB_EVENTS_KEY, type: "active" }),
        qc.refetchQueries({ queryKey: CONTENT_HUB_SEARCH_KEY, type: "active" }),
      ]);
      this.lastPolledAt = Date.now();
    };
    // Fire once immediately so fresh data lands as soon as fallback engages.
    void tick();
    this.pollingTimer = setInterval(() => void tick(), POLL_INTERVAL_MS);
  }

  /** Leave polling fallback (SSE confirmed back, or teardown). Clears the timer
   *  and resets the failure streak. */
  private exitPollingMode() {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer);
      this.pollingTimer = null;
    }
    this.lastPolledAt = null;
    this.nextPollAt = null;
    if (!this.pollingMode) return;
    this.pollingMode = false;
    this.failureCount = 0;
    this.notifyPollingModeListeners();
  }

  getPollingMode = (): boolean => this.pollingMode;

  /** Subscribe to polling-mode transitions (useSyncExternalStore-compatible). */
  onPollingModeChange = (listener: () => void): (() => void) => {
    this.pollingModeListeners.add(listener);
    return () => this.pollingModeListeners.delete(listener);
  };

  private notifyPollingModeListeners() {
    this.pollingModeListeners.forEach((listener) => {
      try {
        listener();
      } catch {
        // A bad listener must not break the others.
      }
    });
  }

  // ── Global status panel: metrics + user actions ────────────────────────────

  /** A fresh snapshot of connection health for the status panel. Returns a new
   *  object each call, so read it on an interval rather than via
   *  useSyncExternalStore (whose snapshot must be referentially stable). */
  getConnectionMetrics = (): ContentHubConnectionMetrics => ({
    status: this.status,
    pollingMode: this.pollingMode,
    connectedSince: this.connectedSince,
    lastFrameAt: this.lastFrameAt,
    lastEventAt: this.lastEventAt,
    nextRetryAt: this.nextRetryAt,
    lastPolledAt: this.lastPolledAt,
    nextPollAt: this.nextPollAt,
    pollIntervalMs: POLL_INTERVAL_MS,
    preferPolling: this.preferPolling,
    activeFilterCount: this.desired.size,
    subscriberCount: this.subscriberCount,
  });

  /** Force an immediate reconnect, bypassing the backoff wait. For the panel's
   *  "retry live" control. No-op when already connected or nothing is mounted. */
  retryLiveNow = (): void => {
    if (this.subscriberCount <= 0) return;
    if (this.preferPolling) return; // pinned to polling — clear the preference first
    this.failureCount = 0;
    this.reconnectAttempt = 0;
    this.nextRetryAt = null;
    this.clearReconnectTimer();
    if (this.status === "connected" && this.eventSource) return;
    if (this.connecting) return; // an attempt is already in flight
    // Drop any half-open socket, then start a fresh two-step connect now.
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
      this.connectionId = null;
      this.stopWatchdog();
      this.clearStabilityReset();
    }
    void this.connect();
  };

  /** Start polling fallback now instead of waiting for the failure threshold, so
   *  a user who accepts a degraded connection gets fresh data immediately. The
   *  background reconnect loop keeps running and upgrades back to live silently. */
  stayInPolling = (): void => {
    if (this.subscriberCount <= 0) return;
    this.enterPollingMode();
  };

  // ── Sticky "prefer polling" preference ─────────────────────────────────────

  getPreferPolling = (): boolean => this.preferPolling;

  /** Subscribe to preference changes (useSyncExternalStore-compatible). */
  onPreferPollingChange = (listener: () => void): (() => void) => {
    this.preferPollingListeners.add(listener);
    return () => this.preferPollingListeners.delete(listener);
  };

  /**
   * Turn the sticky polling preference on or off. When on, the live stream is
   * torn down and the feed stays on polling until this is turned back off —
   * unlike stayInPolling, it survives reconnect attempts (there are none) and
   * page reloads (it is persisted).
   */
  setPreferPolling = (on: boolean): void => {
    if (this.preferPolling === on) return;
    this.preferPolling = on;
    this.writePreferPolling(on);
    this.preferPollingListeners.forEach((listener) => {
      try {
        listener();
      } catch {
        // A bad listener must not break the others.
      }
    });

    if (on) {
      // Drop any live connection and pending reconnect, then pin to polling.
      this.clearReconnectTimer();
      this.resetConfigureRetry();
      if (this.configureTimer) {
        clearTimeout(this.configureTimer);
        this.configureTimer = null;
      }
      if (this.handshakeTimer) {
        clearTimeout(this.handshakeTimer);
        this.handshakeTimer = null;
      }
      this.clearPassTimeout();
      if (this.eventSource) {
        this.eventSource.close();
        this.eventSource = null;
        this.connectionId = null;
        this.stopWatchdog();
        this.clearStabilityReset();
      }
      this.setStatus("idle"); // no live SSE while pinned
      if (this.subscriberCount > 0) this.enterPollingMode();
    } else {
      // Preference cleared — leave polling and restore the live stream.
      this.exitPollingMode();
      this.reconnectAttempt = 0;
      if (
        this.subscriberCount > 0 &&
        !this.eventSource &&
        !this.connecting &&
        !this.reconnectTimer
      ) {
        void this.connect();
      }
    }
  };

  private readPreferPolling(): boolean {
    try {
      return localStorage.getItem(PREFER_POLLING_KEY) === "1";
    } catch {
      return false;
    }
  }

  private writePreferPolling(on: boolean) {
    try {
      if (on) localStorage.setItem(PREFER_POLLING_KEY, "1");
      else localStorage.removeItem(PREFER_POLLING_KEY);
    } catch {
      // Storage unavailable (private mode / blocked) — preference is session-only.
    }
  }

  // ── Visibility-based recovery ──────────────────────────────────────────────

  /** Register the visibilitychange listener once for the manager's lifetime
   *  (module singleton — never removed; teardown must not touch it). */
  private startVisibilityTracking() {
    if (this.visibilityBound || typeof document === "undefined") return;
    if (document.visibilityState === "hidden") {
      this.isHidden = true;
    }
    this.visibilityBound = () => this.handleVisibilityChange();
    document.addEventListener("visibilitychange", this.visibilityBound);
  }

  /** While hidden, timers freeze and frame delivery is suspended, so the activity
   *  clock can't be trusted and the watchdog stands down. On return we reset the
   *  clock and, if we still hold a socket, arm a short grace probe: a healthy
   *  connection delivers a keepalive ping within a couple of intervals, so
   *  continued silence means the browser dropped the socket without firing
   *  onerror — reconnect then. This replaces the old "hidden longer than the
   *  watchdog window → always reconnect", which needlessly cycled healthy
   *  connections after any brief switch away. */
  private handleVisibilityChange() {
    if (typeof document === "undefined") return;
    if (document.visibilityState === "hidden") {
      this.isHidden = true;
      this.clearVisibilityGrace();
      return;
    }
    const wasHidden = this.isHidden;
    this.isHidden = false;
    // Measure liveness from the return moment, not from a stale pre-hide frame.
    this.recordActivity();
    if (wasHidden && this.subscriberCount > 0 && this.eventSource) {
      this.armVisibilityGrace();
    }
  }

  /** Arm the one-shot tab-return liveness probe (see handleVisibilityChange). */
  private armVisibilityGrace() {
    this.clearVisibilityGrace();
    if (!ACTIVITY_WATCHDOG_ENABLED) return;
    const baseline = this.lastActivityAt;
    this.visibilityGraceTimer = setTimeout(() => {
      this.visibilityGraceTimer = null;
      if (this.isHidden || this.status !== "connected" || !this.eventSource) {
        return;
      }
      // No frame — not even a keepalive ping — since we returned, so the socket
      // died silently while the tab was hidden. Count a failure and reconnect.
      if (this.lastActivityAt === baseline) {
        this.handleFailure();
        this.teardownForReconnect();
      }
    }, VISIBILITY_GRACE_MS);
  }

  private clearVisibilityGrace() {
    if (this.visibilityGraceTimer) {
      clearTimeout(this.visibilityGraceTimer);
      this.visibilityGraceTimer = null;
    }
  }
}

export const contentHubStreamManager = new ContentHubStreamManager();
